import { cross, distance, lerp, sub, type Vec2 } from './vec.js';
import { closestParamOnSegment, type Segment } from './segment.js';
import { pointInPolygon, polygonCentroid, polygonEdges, signedArea } from './polygon.js';

/**
 * Closed faces formed by a set of line segments (planar arrangement). Segments are split at
 * every crossing and at points closer than `tolerance`, dead ends are pruned repeatedly and
 * the remaining cycles are traced. Faces are counter-clockwise and without holes: a face
 * around a separate cycle (e.g. a free-standing box) still includes the area inside it.
 */
export function segmentFaces(segments: readonly Segment[], tolerance = 1e-4): Vec2[][] {
  const segs = segments.filter((s) => distance(s.a, s.b) > tolerance);
  const params = segs.map(() => [0, 1]);
  const addParam = (i: number, t: number) => {
    const len = distance(segs[i]?.a ?? { x: 0, y: 0 }, segs[i]?.b ?? { x: 0, y: 0 });
    if (t >= -tolerance / len && t <= 1 + tolerance / len) params[i]?.push(Math.min(Math.max(t, 0), 1));
  };
  for (let i = 0; i < segs.length; i++) {
    for (let j = i + 1; j < segs.length; j++) {
      const s = segs[i];
      const o = segs[j];
      if (!s || !o) continue;
      const d1 = sub(s.b, s.a);
      const d2 = sub(o.b, o.a);
      const denom = cross(d1, d2);
      const w = sub(o.a, s.a);
      if (Math.abs(denom) > 1e-12 * distance(s.a, s.b) * distance(o.a, o.b)) {
        const t = cross(w, d2) / denom;
        const u = cross(w, d1) / denom;
        const tolS = tolerance / distance(s.a, s.b);
        const tolO = tolerance / distance(o.a, o.b);
        if (t >= -tolS && t <= 1 + tolS && u >= -tolO && u <= 1 + tolO) {
          addParam(i, t);
          addParam(j, u);
        }
      } else if (Math.abs(cross(w, d1)) / distance(s.a, s.b) <= tolerance) {
        // Collinear overlap: each segment is split where the other one ends.
        for (const p of [o.a, o.b]) if (near(p, s, tolerance)) addParam(i, closestParamOnSegment(p, s));
        for (const p of [s.a, s.b]) if (near(p, o, tolerance)) addParam(j, closestParamOnSegment(p, o));
      }
    }
  }

  const vertices: Vec2[] = [];
  const vertexAt = (p: Vec2) => {
    const found = vertices.findIndex((v) => distance(v, p) <= tolerance);
    if (found >= 0) return found;
    vertices.push(p);
    return vertices.length - 1;
  };
  const edges = new Map<string, [number, number]>();
  segs.forEach((s, i) => {
    const ts = [...new Set(params[i])].sort((a, b) => a - b);
    let prev = -1;
    for (const t of ts) {
      const v = vertexAt(lerp(s.a, s.b, t));
      if (prev >= 0 && prev !== v) edges.set(prev < v ? `${String(prev)}-${String(v)}` : `${String(v)}-${String(prev)}`, [prev, v]);
      prev = v;
    }
  });

  const adjacency = vertices.map(() => new Set<number>());
  for (const [a, b] of edges.values()) {
    adjacency[a]?.add(b);
    adjacency[b]?.add(a);
  }
  // Dead ends cannot bound a face; removing one may expose the next.
  const stack = adjacency.flatMap((n, i) => (n.size === 1 ? [i] : []));
  while (stack.length > 0) {
    const v = stack.pop() ?? 0;
    const n = adjacency[v];
    if (n?.size !== 1) continue;
    const [other = 0] = n;
    n.clear();
    const on = adjacency[other];
    on?.delete(v);
    if (on?.size === 1) stack.push(other);
  }

  const angle = (from: number, to: number) => {
    const a = vertices[from] ?? { x: 0, y: 0 };
    const b = vertices[to] ?? { x: 0, y: 0 };
    return Math.atan2(b.y - a.y, b.x - a.x);
  };
  const sorted = adjacency.map((n, v) => [...n].sort((a, b) => angle(v, a) - angle(v, b)));
  const visited = new Set<string>();
  const faces: Vec2[][] = [];
  sorted.forEach((neighbours, start) => {
    for (const first of neighbours) {
      if (visited.has(`${String(start)}>${String(first)}`)) continue;
      const ring: Vec2[] = [];
      let u = start;
      let v = first;
      // With the interior on the left, the next edge is the one just clockwise of the way back.
      while (!visited.has(`${String(u)}>${String(v)}`)) {
        visited.add(`${String(u)}>${String(v)}`);
        ring.push(vertices[u] ?? { x: 0, y: 0 });
        const around = sorted[v] ?? [];
        const back = around.indexOf(u);
        const w = around[(back - 1 + around.length) % around.length] ?? u;
        u = v;
        v = w;
      }
      if (signedArea(ring) > tolerance * tolerance) faces.push(ring);
    }
  });
  return faces;
}

const near = (p: Vec2, s: Segment, tolerance: number) =>
  distance(p, lerp(s.a, s.b, closestParamOnSegment(p, s))) <= tolerance;

/**
 * A point well inside a polygon with holes, e.g. for a label: the centroid if it lies inside,
 * otherwise the middle of the widest horizontal span through the shape.
 */
export function interiorPoint(outer: readonly Vec2[], holes: readonly (readonly Vec2[])[] = []): Vec2 {
  const inside = (p: Vec2) => pointInPolygon(p, outer) && holes.every((h) => !pointInPolygon(p, h));
  const c = polygonCentroid(outer);
  if (inside(c)) return c;
  const ys = outer.map((p) => p.y);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const edges = [outer, ...holes].flatMap(polygonEdges);
  let best: { p: Vec2; width: number } | null = null;
  for (const f of [0.5, 0.25, 0.75, 0.125, 0.375, 0.625, 0.875]) {
    const y = y0 + (y1 - y0) * f;
    const xs = edges
      .filter((e) => (e.a.y <= y) !== (e.b.y <= y))
      .map((e) => e.a.x + ((y - e.a.y) / (e.b.y - e.a.y)) * (e.b.x - e.a.x))
      .sort((a, b) => a - b);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const x0 = xs[i] ?? 0;
      const x1 = xs[i + 1] ?? 0;
      if (!best || x1 - x0 > best.width) best = { p: { x: (x0 + x1) / 2, y }, width: x1 - x0 };
    }
  }
  return best?.p ?? c;
}
